import { UserType } from '../enums/user-type.enum'
import type AppUser from '../models/app-user.model'

import * as AppUserLocalStorageConstants from '../constants/app-user-local-storage-constants'
import UserApiService from '../services/user-api.service'

// Permission categories for better organization
export const PermissionCategories = {
  USER: ['user.create', 'user.view', 'user.edit', 'user.delete', 'user.import'],
  STATION: [
    'station.create',
    'station.view',
    'station.edit',
    'station.delete',
    'station.manage',
    'station.import',
    'station.export',
  ],
  FUEL: [
    'fuel_type.create',
    'fuel_type.view',
    'fuel_type.edit',
    'fuel_type.delete',
    'fuel_pump.create',
    'fuel_pump.view',
    'fuel_pump.edit',
    'fuel_pump.delete',
    'fuel_pump.import',
    'fuel_pump.update_price',
  ],
  SERVICE: [
    'service.create',
    'service.view',
    'service.edit',
    'service.delete',
    'service.toggle_status',
    'service.statistics',
  ],
  REPORT: [
    'report.view',
    'report.customers',
    'report.vouchers',
    'report.transactions',
    'report.reviews',
    'report.promotions',
  ],
  CUSTOMER: ['customer.view', 'customer.edit', 'customer.statistics', 'customer.status_update'],
  VOUCHER: [
    'voucher.create',
    'voucher.view',
    'voucher.edit',
    'voucher.delete',
    'voucher.block',
    'voucher.release',
    'voucher.resend_link',
  ],
  TRANSACTION: [
    'transaction.view',
    'transaction.edit',
    'transaction.reprocess',
    'transaction.check_status',
  ],
  REVIEW: ['review.create', 'review.view', 'review.edit', 'review.delete', 'review.pending'],
  STATISTICS: [
    'statistics.view',
    'statistics.vouchers',
    'statistics.transactions',
    'statistics.customers',
    'statistics.combined',
  ],
  ANNOUNCEMENT: [
    'announcement.create',
    'announcement.view',
    'announcement.edit',
    'announcement.delete',
    'announcement.publish',
    'announcement.unpublish',
  ],
  BROADCAST: ['broadcast.create', 'broadcast.view', 'broadcast.send', 'broadcast.cancel'],
}

export const useAppUserStore = defineStore('appUserStore', () => {
  const userApiService = new UserApiService()

  // Load encrypted data from localStorage
  const encryptedAppUser = localStorage.getItem(AppUserLocalStorageConstants.APP_USER)
  const encryptedPermissions = localStorage.getItem(AppUserLocalStorageConstants.PERMISSIONS)

  // Decrypt and parse data
  const decryptedAppUser = encryptedAppUser ? (parseAccessToken(encryptedAppUser) as AppUser) : null
  const decryptedPermissions = encryptedPermissions
    ? (JSON.parse(encryptedPermissions) as string[])
    : []

  // State
  const mAppUser = ref<AppUser | null>(decryptedAppUser)
  const mPermissions = ref<string[]>(decryptedPermissions)

  const setAppUser = (appUser: AppUser, permissions: string[]) => {
    mAppUser.value = appUser
    mPermissions.value = permissions

    // Store in localStorage
    localStorage.setItem(AppUserLocalStorageConstants.APP_USER, stringifyAccessToken(appUser))
    localStorage.setItem(AppUserLocalStorageConstants.PERMISSIONS, JSON.stringify(permissions))
  }

  const removeAppUser = () => {
    mAppUser.value = null
    mPermissions.value = []
    localStorage.removeItem(AppUserLocalStorageConstants.APP_USER)
    localStorage.removeItem(AppUserLocalStorageConstants.PERMISSIONS)
  }

  // Basic user checks
  const isUserEmpty = computed<boolean>(() => mAppUser.value != null)

  // Role-based access control
  const hasRole = (role: string): boolean => {
    if (!isUserEmpty.value) return false
    return mAppUser.value?.role === role
  }
  // Permission-based access control
  const hasPermission = (permission: string): boolean => {
    if (!isUserEmpty.value) return false
    return mPermissions.value.includes(permission)
  }

  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!isUserEmpty.value) return false
    return permissions.some(permission => mPermissions.value.includes(permission))
  }

  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!isUserEmpty.value) return false
    return permissions.every(permission => mPermissions.value.includes(permission))
  }

  // Category-based permission checks
  const hasAccessToCategory = (category: string[]): boolean => {
    return hasAnyPermission(category)
  }

  // Specific feature access checks
  const canManageUsers = computed(() => hasAnyPermission(PermissionCategories.USER))
  const canManageStations = computed(() => hasAnyPermission(PermissionCategories.STATION))
  const canManageFuel = computed(() => hasAnyPermission(PermissionCategories.FUEL))
  const canManageServices = computed(() => hasAnyPermission(PermissionCategories.SERVICE))
  const canViewReports = computed(() => hasAnyPermission(PermissionCategories.REPORT))
  const canManageCustomers = computed(() => hasAnyPermission(PermissionCategories.CUSTOMER))
  const canManageVouchers = computed(() => hasAnyPermission(PermissionCategories.VOUCHER))
  const canManageTransactions = computed(() => hasAnyPermission(PermissionCategories.TRANSACTION))
  const canManageReviews = computed(() => hasAnyPermission(PermissionCategories.REVIEW))
  const canViewStatistics = computed(() => hasAnyPermission(PermissionCategories.STATISTICS))
  const canManageAnnouncements = computed(() => hasAnyPermission(PermissionCategories.ANNOUNCEMENT))
  const canManageBroadcasts = computed(() => hasAnyPermission(PermissionCategories.BROADCAST))

  const isSuperAdmin = computed(() => hasRole(UserType.SUPER_ADMIN))
  const isAdmin = computed(() => hasRole(UserType.ADMIN))
  const isFinance = computed(() => hasRole(UserType.FINANCE))
  const isAdminOrSuperAdmin = computed(() => isSuperAdmin.value || isAdmin.value || isFinance.value)

  // Get all permissions
  const permissions = computed(() => mPermissions.value)

  // User info
  const currentUser = computed(() => mAppUser.value)
  const userFullName = computed(() => {
    if (!mAppUser.value) return ''
    return `${mAppUser.value.firstName} ${mAppUser.value.lastName}`
  })

  const updateFmcToken = async (token: string) => {
    const appUser = mAppUser.value
    if (appUser) {
      const regulatorCompanyId = 0
      const employeeId = appUser.id

      if (regulatorCompanyId) {
        const result = await userApiService.updateFmcToken(
          '',
          regulatorCompanyId,
          employeeId,
          token
        )

        result.fold(
          value => logDebug('AppUserStore', 'fmc_token updated for:', value),
          error => logError('AppUserStore', 'fmc_token error:', error)
        )
      }
    }
  }

  function stringifyAccessToken(accessToken: AppUser) {
    return JSON.stringify(accessToken)
  }

  function parseAccessToken(jsonStr: string | null) {
    return jsonStr && jsonStr.length > 0 ? JSON.parse(jsonStr) : null
  }

  return {
    // State
    appUser: mAppUser,
    permissions,
    currentUser,
    userFullName,

    // Actions
    setAppUser,
    removeAppUser,
    updateFmcToken,

    // Basic checks
    isUserEmpty,

    hasRole,

    // Permission methods
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasAccessToCategory,

    // Category access
    canManageUsers,
    canManageStations,
    canManageFuel,
    canManageServices,
    canViewReports,
    canManageCustomers,
    canManageVouchers,
    canManageTransactions,
    canManageReviews,
    canViewStatistics,
    canManageAnnouncements,
    canManageBroadcasts,

    isAdmin,
    isSuperAdmin,
    isAdminOrSuperAdmin,
  }
})
